import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Field, Card, SaveButton, GhostButton, inputCls } from "../ui";
import { adminFetch } from "../adminApi";

export default function AdminSettings({ content, reload }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(content.settings || {})));
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (key, value) => setDraft((d) => ({ ...d, [key]: value }));
  const updateHour = (idx, key, value) =>
    setDraft((d) => ({ ...d, hours: (d.hours || []).map((h, i) => (i === idx ? { ...h, [key]: value } : h)) }));
  const addHour = () => setDraft((d) => ({ ...d, hours: [...(d.hours || []), { label: "Mo–Fr", value: "09:00–18:00" }] }));
  const removeHour = (idx) => setDraft((d) => ({ ...d, hours: (d.hours || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    setSaving(true);
    try {
      await adminFetch("/admin/content/settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(draft),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      await reload();
      toast.success("Einstellungen gespeichert – live auf der Website.");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <Card title="Zentrale Website-Einstellungen">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Firmenname">
            <input data-testid="admin-settings-company" className={inputCls} value={draft.company_name || ""} onChange={(e) => update("company_name", e.target.value)} maxLength={80} />
          </Field>
          <Field label="Inhaber (für Impressum & Datenschutz)">
            <input data-testid="admin-settings-owner" className={inputCls} value={draft.owner_name || ""} onChange={(e) => update("owner_name", e.target.value)} maxLength={80} />
          </Field>
          <Field label="E-Mail-Adresse">
            <input type="email" data-testid="admin-settings-email" className={inputCls} value={draft.email || ""} onChange={(e) => update("email", e.target.value)} />
          </Field>
          <Field label="Telefon (Anzeige, z. B. 01590 6766314)">
            <input data-testid="admin-settings-phone-display" className={inputCls} value={draft.phone_display || ""} onChange={(e) => update("phone_display", e.target.value)} maxLength={40} />
          </Field>
          <Field label="Telefon (Link-Format, nur Ziffern)">
            <input data-testid="admin-settings-phone-href" className={inputCls} value={draft.phone_href || ""} onChange={(e) => update("phone_href", e.target.value)} maxLength={40} />
          </Field>
          <Field label="WhatsApp (nur Ziffern, leer = ausblenden)">
            <input data-testid="admin-settings-whatsapp" className={inputCls} value={draft.whatsapp || ""} onChange={(e) => update("whatsapp", e.target.value)} maxLength={40} placeholder="z. B. 4915906766314" />
          </Field>
          <Field label="Antwortzeit (optional)">
            <input data-testid="admin-settings-response" className={inputCls} value={draft.response_time || ""} onChange={(e) => update("response_time", e.target.value)} maxLength={120} placeholder="z. B. Antwort am selben Tag" />
          </Field>
          <Field label="Einsatzgebiet (Anzeige)">
            <input data-testid="admin-settings-area" className={inputCls} value={draft.area || ""} onChange={(e) => update("area", e.target.value)} maxLength={120} />
          </Field>
          <Field label="Anschrift">
            <input data-testid="admin-settings-address" className={inputCls} value={draft.address || ""} onChange={(e) => update("address", e.target.value)} maxLength={200} />
          </Field>
          <Field label="Instagram-Link">
            <input className={inputCls} value={draft.instagram || ""} onChange={(e) => update("instagram", e.target.value)} maxLength={300} />
          </Field>
          <Field label="TikTok-Link">
            <input className={inputCls} value={draft.tiktok || ""} onChange={(e) => update("tiktok", e.target.value)} maxLength={300} />
          </Field>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Diese Kontaktdaten werden auf der gesamten Website zentral verwendet. Ohne Telefonnummer
          werden keine Telefon-Buttons angezeigt (keine defekten Links).
        </p>
      </Card>

      <Card title="Verfügbarkeit / Zeiten" actions={<GhostButton onClick={addHour} testid="admin-hours-add">+ Zeile</GhostButton>}>
        {(draft.hours || []).map((h, idx) => (
          <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <Field label="Bezeichnung (z. B. Mo–Fr)">
              <input className={inputCls} value={h.label || ""} onChange={(e) => updateHour(idx, "label", e.target.value)} maxLength={60} />
            </Field>
            <Field label="Zeiten (z. B. 09:00–18:00)">
              <input className={inputCls} value={h.value || ""} onChange={(e) => updateHour(idx, "value", e.target.value)} maxLength={80} />
            </Field>
            <GhostButton danger onClick={() => removeHour(idx)} testid={`admin-hours-delete-${idx}`}>
              ×
            </GhostButton>
          </div>
        ))}
        {!draft.hours?.length && <p className="text-xs text-zinc-400">Keine Zeiten hinterlegt.</p>}
      </Card>

      <Card title="Noch offen für den Livegang (Rechtliches)">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
          <ul className="text-sm text-zinc-300 leading-relaxed flex flex-col gap-1.5">
            <li>Datenschutzerklärung vor Livegang mit eingesetzten Diensten abgleichen und rechtlich prüfen lassen</li>
            <li>Impressum: vollständig (Inhaber Lukas Wiese, Kleinunternehmer § 19 UStG, § 55 Abs. 2 RStV – beim Betreiber abgefragt)</li>
          </ul>
        </div>
        <p className="text-xs text-zinc-400">
          Die Rechtstexte bleiben Entwürfe ohne Rechtsberatung; gelb markierte Rest-Platzhalter sind
          auf den Rechtsseiten sichtbar, solange etwas offen ist.
        </p>
      </Card>

      <div className="sticky bottom-4 flex items-center gap-3 rounded-2xl border border-[#27272A] bg-[#0C0C0E]/95 p-4 backdrop-blur">
        <SaveButton onClick={save} saving={saving} saved={saved} testid="admin-save-settings" />
        <GhostButton onClick={() => setDraft(JSON.parse(JSON.stringify(content.settings || {})))} testid="admin-reset-settings">
          Verwerfen
        </GhostButton>
      </div>
    </div>
  );
}
